import React, { useEffect, useState } from 'react';
import { getTenantInfo, getServices } from '../api';

function ServiceCard({ s, primaryColor, onBook }) {
  const [expanded, setExpanded] = useState(false);
  const MAX_DESC = 100;
  const desc = s.description || '';
  const truncated = desc.length > MAX_DESC && !expanded;

  const formatPrice = (cents) => cents === 0 ? 'Free' : `$${(cents / 100).toFixed(2)}`;
  const formatDuration = (min) =>
    min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ''}` : `${min} min`;

  return (
    <div style={{
      border: '1px solid #e5e7eb',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      background: 'white',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Image */}
      {s.image_url ? (
        <img
          src={s.image_url}
          alt={s.name}
          style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block' }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div style={{
          width: '100%', height: '200px',
          background: s.color || primaryColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ color: 'white', fontSize: '2.5rem', fontWeight: 700 }}>{s.name[0]}</span>
        </div>
      )}

      {/* Body */}
      <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem', fontWeight: 700, color: '#111827' }}>
          {s.name}
        </h3>

        {desc && (
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 0.75rem', lineHeight: 1.55 }}>
            {truncated ? desc.slice(0, MAX_DESC) + '…' : desc}
            {desc.length > MAX_DESC && (
              <button
                onClick={() => setExpanded(x => !x)}
                style={{ background: 'none', border: 'none', color: primaryColor, cursor: 'pointer', fontSize: '0.875rem', padding: '0 0 0 4px', fontWeight: 600 }}
              >
                {expanded ? 'less' : 'more'}
              </button>
            )}
          </p>
        )}

        {/* Duration + Price row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', marginTop: 'auto' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#6b7280', fontSize: '0.875rem' }}>
            {/* Clock icon */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            {formatDuration(s.duration_minutes)}
          </span>
          <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#111827', fontSize: '1rem' }}>
            {formatPrice(s.price_cents)}
          </span>
        </div>

        <button
          onClick={() => onBook(s.id)}
          style={{
            width: '100%',
            background: primaryColor,
            color: 'white',
            border: 'none',
            padding: '0.65rem',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.9rem',
          }}
        >
          Book Now
        </button>
      </div>
    </div>
  );
}

export default function ServicesGallery({ slug }) {
  const [services, setServices] = useState([]);
  const [tenant,   setTenant]   = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!slug) return;
    Promise.all([getTenantInfo(slug), getServices(slug)])
      .then(([t, s]) => { setTenant(t); setServices(s); })
      .finally(() => setLoading(false));
  }, [slug]);

  const bookNow = (serviceId) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('view');
    if (serviceId) url.searchParams.set('serviceId', serviceId);
    window.location.href = url.toString();
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '2rem', fontFamily: 'system-ui, sans-serif', color: '#6b7280' }}>
      Loading…
    </div>
  );

  if (!slug) return (
    <div style={{ textAlign: 'center', padding: '2rem', color: '#ef4444', fontFamily: 'system-ui, sans-serif' }}>
      No tenant specified.
    </div>
  );

  const primaryColor = tenant?.brand_color || tenant?.primary_color || '#2E5BBA';

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', padding: '1.5rem', maxWidth: '1100px', margin: '0 auto' }}>
      {tenant && (
        <h1 style={{ textAlign: 'center', marginBottom: '0.25rem', fontSize: '1.75rem', fontWeight: 700, color: '#111827' }}>
          {tenant.name}
        </h1>
      )}
      <p style={{ textAlign: 'center', marginBottom: '2rem', color: '#6b7280', fontWeight: 400, fontSize: '1.05rem', marginTop: '0.25rem' }}>
        Our Services
      </p>

      {services.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#9ca3af' }}>No services available.</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: '1.5rem',
        }}>
          {services.map(s => (
            <ServiceCard key={s.id} s={s} primaryColor={primaryColor} onBook={bookNow} />
          ))}
        </div>
      )}
    </div>
  );
}
